// ============================================================================
// Cost Tracking (route: /admin/infrastructure-pricing) — two tabs:
//   • Pricing          — the ai_model_pricing rate matrix editor.
//   • Per-tenant usage — each tenant's spend @ current rates; expand a row to
//                        drill into that tenant's full cost panel inline
//                        (chart + category legend + per-feature + line items).
//
// The rate editor is AISettingsPricingSection (kept under that filename for git
// history). The usage table reuses fetchTenantCostsRollup; the inline drill-down
// reuses the shared TenantCostPanel — the very same component the tenant detail
// Costs tab renders — so both surfaces stay identical.
// ============================================================================

import { Fragment, useEffect, useState } from 'react';
import { ChevronRight, RefreshCw, Loader2 } from 'lucide-react';
import AISettingsPricingSection from './AISettingsPricingSection';
import { fetchTenantCostsRollup, type TenantCostRow } from '@/lib/api';
import { TenantCostPanel } from '@/components/tenants/TenantCostPanel';

function formatCostUsd(cents: number): string {
  if (!cents) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function formatTokens(n: number): string {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function InfrastructurePricingPage() {
  const [tab, setTab] = useState<'pricing' | 'tenants'>('pricing');
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Cost Tracking</h1>
        <p className="text-surface-500 mt-1 text-sm">
          Model rate matrix and per-tenant spend at those rates. Expand a tenant
          to drill into its full cost breakdown. Edited rates take effect on the
          next worker cold start (≤ 15 min in-memory TTL in the App's loggers).
        </p>
      </div>

      <div className="flex gap-4 border-b border-surface-200 text-sm">
        {([['pricing', 'Pricing'], ['tenants', 'Per-tenant usage']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={'pb-2 -mb-px border-b-2 ' + (tab === id
              ? 'border-primary text-surface-900 font-medium'
              : 'border-transparent text-surface-500 hover:text-surface-700')}
          >{label}</button>
        ))}
      </div>

      {tab === 'pricing' ? <AISettingsPricingSection /> : <TenantCostUsageTable />}
    </div>
  );
}

function TenantCostUsageTable() {
  const [rows, setRows] = useState<TenantCostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchTenantCostsRollup(30)
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [tick]);

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Server returns rows for any tenant with at least one charge; sort
  // by spend desc so the heaviest customers float to the top.
  const sorted = [...rows].sort((a, b) => b.total_cents - a.total_cents);
  const grand = sorted.reduce((s, r) => s + r.total_cents, 0);

  return (
    <div className="glass-panel p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-surface-900">Per-tenant usage @ current prices</h2>
          <p className="text-surface-500 mt-1 text-xs">
            Last 30 days. Costs are recomputed from the rate matrix on every refresh —
            edit a row, then reload to see the new estimate. Click a tenant to expand its full cost breakdown.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setTick(t => t + 1)}
          className="text-xs text-surface-500 hover:text-surface-900 inline-flex items-center gap-1.5 shrink-0"
          disabled={loading}
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-surface-500" /></div>
      ) : error ? (
        <div className="p-3 rounded-lg bg-rose-50 text-rose-700 text-sm">{error}</div>
      ) : sorted.length === 0 ? (
        <div className="text-sm text-surface-500 py-6 text-center">
          No tenant usage recorded in the last 30 days.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-surface-500 border-b border-surface-200">
                <th className="py-2 pr-3">Tenant</th>
                <th className="py-2 px-3 text-right">LLM tokens (in / out)</th>
                <th className="py-2 px-3 text-right">LLM</th>
                <th className="py-2 px-3 text-right">Image</th>
                <th className="py-2 px-3 text-right">Video</th>
                <th className="py-2 px-3 text-right">Audio</th>
                <th className="py-2 px-3 text-right">Other</th>
                <th className="py-2 pl-3 text-right">Total</th>
                <th className="py-2 pl-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                const isOpen = expanded.has(r.tenant_id);
                return (
                  <Fragment key={r.tenant_id}>
                    <tr
                      className="border-b border-surface-100 last:border-0 hover:bg-surface-50/60 transition-colors cursor-pointer"
                      onClick={() => toggle(r.tenant_id)}
                    >
                      <td className="py-2.5 pr-3 font-medium text-surface-900">{r.tenant_name}</td>
                      <td className="py-2.5 px-3 text-right text-surface-700 tabular-nums">
                        {formatTokens(r.prompt_tokens)} / {formatTokens(r.completion_tokens)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-surface-700 tabular-nums">{formatCostUsd(r.llm_cents)}</td>
                      <td className="py-2.5 px-3 text-right text-surface-700 tabular-nums">{formatCostUsd(r.image_cents)}</td>
                      <td className="py-2.5 px-3 text-right text-surface-700 tabular-nums">{formatCostUsd(r.video_cents)}</td>
                      <td className="py-2.5 px-3 text-right text-surface-700 tabular-nums">{formatCostUsd(r.audio_cents)}</td>
                      <td className="py-2.5 px-3 text-right text-surface-700 tabular-nums">{formatCostUsd(r.other_3p_cents)}</td>
                      <td className="py-2.5 pl-3 text-right font-semibold text-surface-900 tabular-nums">{formatCostUsd(r.total_cents)}</td>
                      <td className="py-2.5 pl-3 text-surface-400">
                        <ChevronRight size={14} className={'transition-transform ' + (isOpen ? 'rotate-90' : '')} />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-surface-50/40">
                        <td colSpan={9} className="px-2 pb-4 pt-1">
                          <TenantCostPanel tenantId={r.tenant_id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              <tr className="bg-surface-50/80">
                <td className="py-2.5 pr-3 font-semibold text-surface-900">Total</td>
                <td className="py-2.5 px-3"></td>
                <td className="py-2.5 px-3"></td>
                <td className="py-2.5 px-3"></td>
                <td className="py-2.5 px-3"></td>
                <td className="py-2.5 px-3"></td>
                <td className="py-2.5 px-3"></td>
                <td className="py-2.5 pl-3 text-right font-bold text-surface-900 tabular-nums">{formatCostUsd(grand)}</td>
                <td className="py-2.5 pl-3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
